/**
 * @fileoverview AR-924 — One row of the step-1 list: thumbnail, name + meta, progress track, status, remove.
 */

import { ActionIcon, Box, Group, Progress, Text } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { MAX_FILE_SIZE_MB, formatFileSize } from '../teacherUpload'
import { StagedThumb } from './StagedThumb'
import type { StagedItem } from './stagedItems'

type Translate = (key: string, options?: object) => string

const REMOVE_ICON_SIZE = 14
/** Figma: progress track takes the middle third of the row. */
const TRACK_WIDTH = 220
const STATUS_LABEL_MIN_WIDTH = 96
const FULL_TRACK = 100
const FAILED_ROW_TINT_ALPHA = 0.6
/** Lets the name column shrink and truncate instead of pushing the track out of the row. */
const GROW_AND_TRUNCATE = { flex: '1 1 0%', minWidth: 0 }

const STATUS_COLOR: Record<StagedItem['status'], string> = {
  ready: 'gray',
  uploading: 'blue',
  uploaded: 'green',
  added: 'green',
  failed: 'red',
}

function failReasonText(item: StagedItem, tr: Translate): string | null {
  if (item.failReason === 'too_large') return tr('failed_too_large', { mb: MAX_FILE_SIZE_MB })
  if (item.failReason === 'type_not_allowed') return tr('failed_type')
  if (item.failReason === 'upload') return tr('failed_upload')
  return null
}

function statusText(item: StagedItem, tr: Translate): string {
  if (item.status === 'uploading') return tr('status_uploading', { percent: item.percent })
  if (item.failReason === 'upload') return tr('status_upload_failed')
  return tr(`status_${item.status}`)
}

function metaText(item: StagedItem, studentName: string | null, tr: Translate): string {
  if (item.kind === 'file') return formatFileSize(item.sizeInBytes)
  if (studentName) return tr('link_for', { name: studentName })
  return tr('link_unassigned')
}

function trackValue(item: StagedItem): number {
  if (item.status === 'uploaded' || item.status === 'added') return FULL_TRACK
  return item.percent
}

interface StagedItemRowProps {
  readonly item: StagedItem
  readonly studentName: string | null
  readonly disabled: boolean
  readonly onRemove: (id: string) => void
}

export function StagedItemRow({ item, studentName, disabled, onRemove }: StagedItemRowProps) {
  const { t } = useTranslation()
  const tr: Translate = (key, options) => t(`task_tracker.upload_for_class.${key}`, options)
  const failed = item.status === 'failed'
  const secondLine = failReasonText(item, tr) ?? metaText(item, studentName, tr)

  return (
    <Group
      noWrap
      spacing="md"
      px="sm"
      py="xs"
      sx={theme => ({
        borderBottom: `1px solid ${theme.colors.gray[2]}`,
        background: failed ? theme.fn.rgba(theme.colors.red[0], FAILED_ROW_TINT_ALPHA) : undefined,
      })}
    >
      <Group spacing="sm" noWrap style={GROW_AND_TRUNCATE}>
        <StagedThumb item={item} />
        <Box sx={GROW_AND_TRUNCATE}>
          <Text size="sm" weight={500} truncate>{item.name}</Text>
          <Text size="xs" color={failed ? 'red' : 'dimmed'} weight={failed ? 600 : 400} truncate>
            {secondLine}
          </Text>
        </Box>
      </Group>
      <Progress
        value={trackValue(item)}
        size="sm"
        radius="xl"
        color={STATUS_COLOR[item.status]}
        sx={{ width: TRACK_WIDTH, flexShrink: 0 }}
      />
      <Text
        size="sm"
        weight={600}
        color={STATUS_COLOR[item.status]}
        sx={{ minWidth: STATUS_LABEL_MIN_WIDTH, textAlign: 'right', flexShrink: 0 }}
      >
        {statusText(item, tr)}
      </Text>
      <ActionIcon
        variant="subtle"
        color={failed ? 'red' : 'gray'}
        onClick={() => onRemove(item.id)}
        disabled={disabled}
        aria-label={tr('remove')}
      >
        <IconX size={REMOVE_ICON_SIZE} />
      </ActionIcon>
    </Group>
  )
}
